// Copyright (c) 2025-2026 Motswagae Holding (Pty) Ltd. All Rights Reserved.
// CIPC Registration: 2019/443098/07
// Author: Velly Sipho Phaloane
//
// This source code is the proprietary and confidential property of
// Motswagae Holding (Pty) Ltd, trading as 360Wallet.
// Unauthorized copying, modification, distribution, or use of this
// software, in whole or in part, is strictly prohibited.
//
// Protected under the Copyright Act 98 of 1978 (South Africa)
// and international copyright treaties.
import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { Plus, Network, Edit2, Trash2, Mail, DollarSign } from "lucide-react";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Tier3Operators = () => {
  const [operators, setOperators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingOperator, setEditingOperator] = useState(null);
  const [formData, setFormData] = useState({
    operator_id: "",
    operator_name: "",
    description: "",
    billing_address: "",
    billing_email_to: "",
    billing_email_cc: "",
    billing_email_bcc: ""
  });

  const fetchOperators = useCallback(async () => {
    try {
      const token = localStorage.getItem("360wallet_token");
      if (!token) {
        console.error("No token found in localStorage");
        toast.error("Please login again");
        return;
      }
      const response = await axios.get(`${API_URL}/api/billing/tier3-operators`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOperators(response.data);
    } catch (error) {
      console.error("Fetch operators error:", error.response?.data || error.message);
      if (error.response?.status === 401) {
        toast.error("Session expired. Please login again.");
      } else {
        toast.error("Failed to load ATM Acquirers");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOperators();
  }, [fetchOperators]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("360wallet_token");
    
    const payload = {
      ...formData,
      billing_email_to: formData.billing_email_to ? formData.billing_email_to.split(",").map(e => e.trim()).filter(Boolean) : [],
      billing_email_cc: formData.billing_email_cc ? formData.billing_email_cc.split(",").map(e => e.trim()).filter(Boolean) : [],
      billing_email_bcc: formData.billing_email_bcc ? formData.billing_email_bcc.split(",").map(e => e.trim()).filter(Boolean) : []
    };

    try {
      if (editingOperator) {
        await axios.put(`${API_URL}/api/billing/tier3-operators/${editingOperator.operator_id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Operator updated successfully");
      } else {
        await axios.post(`${API_URL}/api/billing/tier3-operators`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Operator created successfully");
      }
      setIsDialogOpen(false);
      resetForm();
      fetchOperators();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save operator");
    }
  };

  const handleEdit = (operator) => {
    setEditingOperator(operator);
    setFormData({
      operator_id: operator.operator_id,
      operator_name: operator.operator_name,
      description: operator.description || "",
      billing_address: operator.billing_address || "",
      billing_email_to: (operator.billing_email_to || []).join(", "),
      billing_email_cc: (operator.billing_email_cc || []).join(", "),
      billing_email_bcc: (operator.billing_email_bcc || []).join(", ")
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (operatorId) => {
    if (!window.confirm("Are you sure you want to delete this operator?")) return;
    
    try {
      const token = localStorage.getItem("360wallet_token");
      await axios.delete(`${API_URL}/api/billing/tier3-operators/${operatorId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Operator deleted");
      fetchOperators();
    } catch (error) {
      toast.error("Failed to delete operator");
    }
  };

  const resetForm = () => {
    setFormData({
      operator_id: "",
      operator_name: "",
      description: "",
      billing_address: "",
      billing_email_to: "",
      billing_email_cc: "",
      billing_email_bcc: ""
    });
    setEditingOperator(null);
  };

  const formatCurrency = (amount) => `R${amount.toFixed(2)}`;

  return (
    <div className="space-y-6" data-testid="tier3-operators-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold">ATM Operators</h1>
          <p className="text-muted-foreground">ATM Acquirers & Operators (fee configurable per partner)</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button data-testid="add-operator-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add Acquirer
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editingOperator ? "Edit ATM Operator" : "Add ATM Operator"}</DialogTitle>
              <DialogDescription>
                ATM Operators are billed per successful transaction (fee configurable in Pricing)
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Operator ID</Label>
                  <Input
                    placeholder="e.g., BANKSERV"
                    value={formData.operator_id}
                    onChange={(e) => setFormData({ ...formData, operator_id: e.target.value.toUpperCase() })}
                    disabled={!!editingOperator}
                    required
                    data-testid="operator-id-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Operator Name</Label>
                  <Input
                    placeholder="e.g., BankservAfrica"
                    value={formData.operator_name}
                    onChange={(e) => setFormData({ ...formData, operator_name: e.target.value })}
                    required
                    data-testid="operator-name-input"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Description</Label>
                <Input
                  placeholder="e.g., National Payment Switch Operator"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  data-testid="operator-description-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Billing Address</Label>
                <Input
                  placeholder="Physical address for invoices"
                  value={formData.billing_address}
                  onChange={(e) => setFormData({ ...formData, billing_address: e.target.value })}
                  data-testid="billing-address-input"
                />
              </div>

              <div className="border border-border rounded-lg p-4 space-y-4 bg-secondary/20">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <Mail className="h-4 w-4 text-primary" />
                  Billing Email Configuration
                </div>
                
                <div className="space-y-2">
                  <Label>Email To (comma-separated)</Label>
                  <Input
                    placeholder="billing@company.co.za, finance@company.co.za"
                    value={formData.billing_email_to}
                    onChange={(e) => setFormData({ ...formData, billing_email_to: e.target.value })}
                    data-testid="billing-email-to-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Email CC (comma-separated)</Label>
                  <Input
                    placeholder="manager@company.co.za"
                    value={formData.billing_email_cc}
                    onChange={(e) => setFormData({ ...formData, billing_email_cc: e.target.value })}
                    data-testid="billing-email-cc-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Email BCC (comma-separated)</Label>
                  <Input
                    placeholder="archive@company.co.za"
                    value={formData.billing_email_bcc}
                    onChange={(e) => setFormData({ ...formData, billing_email_bcc: e.target.value })}
                    data-testid="billing-email-bcc-input"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" data-testid="save-operator-btn">
                  {editingOperator ? "Update Acquirer" : "Create Acquirer"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-500/20 rounded-lg">
                <Network className="h-5 w-5 text-purple-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Acquirers</p>
                <p className="text-xl font-mono font-bold">{operators.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-500/20 rounded-lg">
                <DollarSign className="h-5 w-5 text-green-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Billing Rate</p>
                <p className="text-xl font-mono font-bold">Configurable</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/20 rounded-lg">
                <Mail className="h-5 w-5 text-blue-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">With Billing Email</p>
                <p className="text-xl font-mono font-bold">
                  {operators.filter(o => o.billing_email_to?.length > 0).length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Operators Table */}
      <Card className="bg-card border-border">
        <CardContent className="p-0">
          <Table className="data-table">
            <TableHeader>
              <TableRow>
                <TableHead>Operator ID</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Billing Rate</TableHead>
                <TableHead>Billing Email</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8">
                    <div className="animate-pulse text-muted-foreground">Loading operators...</div>
                  </TableCell>
                </TableRow>
              ) : operators.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8">
                    <div className="flex flex-col items-center gap-2">
                      <Network className="h-8 w-8 text-muted-foreground" />
                      <p className="text-muted-foreground">No ATM Acquirers</p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                operators.map((operator) => (
                  <TableRow key={operator.id}>
                    <TableCell>
                      <code className="font-mono text-sm bg-secondary px-2 py-1 rounded">
                        {operator.operator_id}
                      </code>
                    </TableCell>
                    <TableCell className="font-medium">{operator.operator_name}</TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {operator.description || "-"}
                    </TableCell>
                    <TableCell>
                      <span className="font-mono text-purple-400">{formatCurrency(operator.billing_rate)}</span>
                    </TableCell>
                    <TableCell>
                      {operator.billing_email_to?.length > 0 ? (
                        <span className="text-xs text-green-400">
                          {operator.billing_email_to[0]}
                          {operator.billing_email_to.length > 1 && ` +${operator.billing_email_to.length - 1}`}
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground">Not configured</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className={operator.is_active ? "text-green-400" : "text-muted-foreground"}>
                        {operator.is_active ? "Active" : "Inactive"}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(operator)}
                          data-testid={`edit-operator-${operator.operator_id}`}
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:text-destructive"
                          onClick={() => handleDelete(operator.operator_id)}
                          data-testid={`delete-operator-${operator.operator_id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default Tier3Operators;
